import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import queryString from 'query-string'
import s from './Users.scss'
import { Button, Container } from 'reactstrap'
import { Spinner, Tabs } from 'components/ideal-core-ui'
import { makePostRequest, makeGetRequest, makeDeleteRequest } from 'utils/api'
import {
  getTenant,
  getToken,
  setToken,
  setImpersonationData,
  isOnlyTimetracker
} from 'utils/authService'
import { List } from './List'
import { useDispatch } from 'react-redux'
import { PUSH_NOTIFICATION } from 'actions/actionTypes'
import swal from 'sweetalert'
import { AddButton } from 'components/common/AddButton'
import { shouldUpgradeSubscription } from './helpers'
import { SubscriptionUpgrade } from './SubscriptionUpgrade'
import { TeamsList } from './TeamsList'
import { toTeams } from 'utils/serializers'
import { HELP_USER, URLS } from 'utils/constants'
import { getHelpUserEmail, generatePassword } from 'utils/helpers'
import { AddTeam } from './AddTeam'
import { EditTeamModal } from './EditTeamModal'
import { AddUser } from './AddUser'
import { useImmer } from 'use-immer'
import { useLoading } from 'hooks'

const initialRoles = [
  {
    label: 'User',
    description:
      'Can create/edit opportunities, proposals, contacts, companies; view reports, calendar & all normal functionality',
    value: 'ROLE_USER',
    checked: true
  },
  {
    label: 'Proposal Reviewer',
    description:
      'In addition to what a User can do, a Proposal Reviewer can Review and Approve proposals',
    value: 'ROLE_PROPOSALREVIEWER',
    checked: false
  },
  {
    label: 'Administrator',
    description:
      'In addition to what a User and Proposal Reviewer can do, an Administrator can add/remove users, edit organization details, create teams & roles',
    value: 'ROLE_ADMIN',
    checked: false
  }
]

const Users = ({
  setUsers,
  users,
  departments,
  organizationId,
  isFreeTrial,
  subscriptionPlan,
  getSubscriptionInfo
}) => {
  const [formIsVisible, setFormIsVisible] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isConfirmPlanModalVisible, setIsConfirmPlanModalVisible] = useState(false)
  const reduxDispatch = useDispatch()
  const [teams, setTeams] = useState([])
  const [showHelpUserText, setShowHelpUserText] = useState(false)
  const [shouldShowAddHelpUser, setShouldShowAddHelpUser] = useState(false)
  const [isEditTeamModalVisible, setIsEditTeamModalVisible] = useState(false)
  const [editedTeam, setEditedTeam] = useState(null)
  const [error, setError] = useState('')
  const [editedUserDetails, setEditedUserDetails] = useImmer(null)
  const [, withLoadingLocks] = useLoading()

  const location = useLocation()

  const toggleEditTeamModal = () => {
    setIsEditTeamModalVisible(!isEditTeamModalVisible)
  }

  useEffect(() => {
    const search = queryString.parse(location.search)
    if (search.stripe === 'success') {
      setFormIsVisible(true)
    }
  }, [location.search])

  useEffect(() => {
    ;(async () => {
      const teams = await makeGetRequest('/v1/teams')
      setTeams(teams)
    })()
  }, [])

  useEffect(() => {
    setShouldShowAddHelpUser(!users.filter(u => u.email === getHelpUserEmail()).length)
  }, [users])

  const getActiveUsersLength = () =>
    users.filter(u => u.active && u.email !== getHelpUserEmail()).length

  const checkSubscriptionPlan = () => {
    if (!isFreeTrial && shouldUpgradeSubscription(getActiveUsersLength())) {
      setIsConfirmPlanModalVisible(true)
    } else {
      setFormIsVisible(true)
    }
  }

  const removeUser = async (id, email) => {
    const willRemove = await swal({
      title: 'Are you sure you want to mark this user as inactive?',
      buttons: ['No', 'Yes'],
      icon: 'warning'
    })
    if (willRemove) {
      try {
        await makeDeleteRequest(`/v1/users/${id}`)
        await makeDeleteRequest(
          `/v1/tenants?email=${encodeURIComponent(email)}&x-token-tracking=${getToken()}`,
          {
            headers: {
              'x-tenant-schema': getTenant()
            }
          }
        )
        const array = [...users]
        const index = users.findIndex(u => u.id === id)
        if (~index) {
          array[index] = { ...array[index], active: false }
          setUsers(array)
          reduxDispatch({
            type: PUSH_NOTIFICATION,
            payload: {
              title: 'User inactivated successfuly',
              level: 'success'
            }
          })
        }
      } catch (e) {
        reduxDispatch({
          type: PUSH_NOTIFICATION,
          payload: {
            title: 'There was a problem deleting user',
            level: 'error'
          }
        })
      }
    }
  }

  const openEditUser = user => {
    setFormIsVisible(true)
    setIsEdit(true)
    setError('')
    setEditedUserDetails(() => ({
      id: user.id,
      firstName: user.firstName,
      email: user.email,
      lastName: user.lastName,
      teams: toTeams(user.teams) || null,
      department: user.department
        ? {
            value: user.department.id,
            label: user.department.name
          }
        : null,
      phone: user.phone,
      cellPhone: user.cellPhone,
      monthlyQuota: user.monthlyQuota,
      yearlyQuota: user.yearlyQuota,
      yearlyTargetProfit: user.yearlyTargetProfit,
      monthlyTargetProfit: user.monthlyTargetProfit,
      roles: initialRoles.map(role => ({
        ...role,
        checked: user.roles.includes(role.value)
      })),
      validateRoles: initialRoles.filter(role => user.roles.includes(role.value))
    }))
  }

  const resendEmail = async id => {
    try {
      await makePostRequest(`/v1/email/sendEmailUserRegistration?userId=${id}`)
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'Email notification has been sent',
          level: 'success'
        }
      })
    } catch (e) {
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'There was an issue sending the email notification',
          level: 'error'
        }
      })
    }
  }

  const openEditTeamModal = team => {
    setEditedTeam(team)
    toggleEditTeamModal()
  }

  const removeTeam = async id => {
    const willRemove = await swal({
      title: 'Are you sure you want to delete this team?',
      buttons: ['No', 'Yes'],
      icon: 'warning'
    })
    if (willRemove) {
      try {
        await makeDeleteRequest(`/v1/teams/${id}`)
        const array = [...teams]
        const index = teams.findIndex(u => u.id === id)
        if (~index) {
          array.splice(index, 1)
          setTeams(array)
          reduxDispatch({
            type: PUSH_NOTIFICATION,
            payload: {
              title: 'Team deleted successfuly',
              level: 'success'
            }
          })
        }
      } catch (e) {
        reduxDispatch({
          type: PUSH_NOTIFICATION,
          payload: {
            title: 'There was a problem deleting team',
            level: 'error'
          }
        })
      }
    }
  }

  const addHelpUser = async () => {
    const email = getHelpUserEmail()

    const masterSchemaHeader = {
      'x-tenant-schema': 'master'
    }

    const emailExists = await makeGetRequest('/v1/tenants/email', {
      params: {
        email
      },
      headers: masterSchemaHeader
    })

    if (!emailExists) {
      await makePostRequest(
        '/v1/tenants/create',
        {
          tenantId: getTenant(),
          username: email
        },
        {
          headers: masterSchemaHeader
        }
      )
      const response = await makePostRequest('/v1/users/signup', {
        email,
        firstName: HELP_USER.firstName,
        lastName: HELP_USER.lastName,
        organizationId,
        password: generatePassword(14),
        roles: HELP_USER.roles
      })
      setUsers([...users, response.user])
      setShowHelpUserText(false)
    }
  }

  const reactivateUser = async (id, email) => {
    const subscriptionUpgradeInfo =
      subscriptionPlan === 'FREE'
        ? '2-10 Users with $35/month/user'
        : '11+ Users with $30/month/user'
    const popupTitle =
      !isFreeTrial &&
      shouldUpgradeSubscription(getActiveUsersLength()) &&
      !email.includes('@idealcrm.app')
        ? `By reactivating this user you will be going over your limit of active users${' '}
        under the current subscription. Your subscription will be upgrading to ${subscriptionUpgradeInfo}.${' '}
        Would you like to continue with reactivating this user?`
        : 'Are you sure you want to reactivate this user?'
    const willReactivate = await swal({
      title: popupTitle,
      buttons: ['No', 'Yes'],
      icon: 'warning'
    })
    if (willReactivate) {
      try {
        const url = '/v1/tenants/activate'

        const masterSchemaHeader = {
          'x-tenant-schema': getTenant()
        }
        await makePostRequest(
          url,
          { email, 'x-token-tracking': getToken() },
          { headers: masterSchemaHeader }
        )
        await makePostRequest(`/v1/users/${id}/activate`)
        const array = [...users]
        const index = users.findIndex(u => u.id === id)
        if (~index) {
          array[index] = { ...array[index], active: true }
          setUsers(array)
          reduxDispatch({
            type: PUSH_NOTIFICATION,
            payload: {
              title: 'User reactivated successfuly',
              level: 'success'
            }
          })
        }
      } catch (e) {
        reduxDispatch({
          type: PUSH_NOTIFICATION,
          payload: {
            title: 'There was a problem reactivating user',
            level: 'error'
          }
        })
      }
    }
  }

  const impersonateUser = async userId => {
    const willImpersonate = await swal({
      title: 'Are you sure you want to login as this user?',
      text: 'You will be able to see and access everything this user can see.',
      buttons: ['Cancel', 'Login As User'],
      icon: 'warning'
    })

    if (willImpersonate) {
      try {
        // Keep the admin token before it is replaced: exit-impersonation deliberately returns no
        // credentials, so this stored token is the only way back to the admin session.
        const adminToken = getToken()
        const response = await withLoadingLocks(makePostRequest(`/v1/users/impersonate/${userId}`))

        setImpersonationData(adminToken, response.impersonatorEmail)

        setToken(response.token)

        reduxDispatch({
          type: PUSH_NOTIFICATION,
          payload: {
            title: 'Successfully logged in as user',
            level: 'success'
          }
        })

        window.location.href = isOnlyTimetracker() ? URLS.AUTH_TIME_TRACKING : URLS.AUTH_HOME
      } catch (error) {
        console.error('Impersonation failed:', error)
        reduxDispatch({
          type: PUSH_NOTIFICATION,
          payload: {
            title: 'Failed to impersonate user',
            message: error?.response?.data?.message || 'An error occurred',
            level: 'error'
          }
        })
      }
    }
  }

  return (
    <Container>
      <SubscriptionUpgrade
        isConfirmPlanModalVisible={isConfirmPlanModalVisible}
        setIsConfirmPlanModalVisible={setIsConfirmPlanModalVisible}
        subscriptionPlan={subscriptionPlan}
        setFormIsVisible={setFormIsVisible}
      />
      <header className={s.header}>
        <AddButton
          onClick={checkSubscriptionPlan}
          disabled={formIsVisible}
          className={s.greenBtn}
          cy="add_user_btn"
        >
          Add User
        </AddButton>
        {shouldShowAddHelpUser && (
          <Button onClick={() => setShowHelpUserText(true)}>Add iDeal Help User</Button>
        )}
      </header>
      {showHelpUserText && (
        <div className={s.helpUserTextContainer}>
          <div className={s.helpUserText}>
            By selecting Add iDeal Help User, you are giving your consent to create an iDeal Help
            User for training and support purposes only. With this user, an iDeal CRM Support Member
            will be able to customize your system to fit your needs.
            <br />
            This user will not count towards your usage and can be deactivated at any time.
            <br />
            Once you create the user, our support team will be notified and you&apos;ll be contacted
            to discuss your support needs.
            <br />
            <span>
              Click on ‘Create User & Send Notification Email’ button below to create the iDeal Help
              User.
            </span>
          </div>
          <Button onClick={addHelpUser} className={s.greenBtn}>
            {loading ? <Spinner /> : <>Create User & Send Notification Email</>}
          </Button>
        </div>
      )}
      {formIsVisible && (
        <AddUser
          departments={departments}
          isEdit={isEdit}
          getSubscriptionInfo={getSubscriptionInfo}
          organizationId={organizationId}
          setLoading={setLoading}
          loading={loading}
          error={error}
          setError={setError}
          editedUserDetails={editedUserDetails}
          users={users}
          setUsers={setUsers}
          setIsEdit={setIsEdit}
          setFormIsVisible={setFormIsVisible}
          teams={teams}
        />
      )}
      {users.length > 0 && (
        <Tabs
          className={s.tabs}
          noPadding
          keepState
          activeTabKey="active"
          tabs={[
            {
              key: 'active',
              label: 'Active Users',
              value: (
                <List
                  resendEmail={resendEmail}
                  openEditUser={openEditUser}
                  removeUser={removeUser}
                  impersonateUser={impersonateUser}
                  users={users.filter(u => u.active)}
                />
              )
            },
            {
              key: 'inactive',
              label: 'Inactive Users',
              value: <List users={users.filter(u => !u.active)} reactivateUser={reactivateUser} />
            }
          ]}
        />
      )}
      <AddTeam setTeams={setTeams} teams={teams} />
      {teams.length > 0 && (
        <TeamsList teams={teams} removeTeam={removeTeam} openEditTeamModal={openEditTeamModal} />
      )}
      {isEditTeamModalVisible && (
        <EditTeamModal
          editedTeam={editedTeam}
          toggleEditTeamModal={toggleEditTeamModal}
          isEditTeamModalVisible={isEditTeamModalVisible}
          setTeams={setTeams}
          teams={teams}
        />
      )}
    </Container>
  )
}

export default Users
